<template>
  <div v-bind="$attrs" class="w-100">
    <label>{{ label }}</label>
    <v-radio-group
      ref="so-radio-group"
      v-model="internalValue"
      :error="isValid"
      :error-messages="error"
      :disabled="disabled"
      :rules="rules"
      :hide-details="hideDetails"
      :class="{ 'mt-4': !panel, 'apply-panel-focus': panel }"
      validate-on="blur"
    >
      <v-radio
        v-for="(item, index) in items"
        :key="index"
        :value="item.value"
        :disabled="item.disabled"
        :class="{
          'so-radio-panel': panel,
          'so-radio-panel__error': error,
          'so-radio-panel__disabled-message':
            item.disabledRadioMessage && item.disabled,
        }"
        color="success"
        data-testid="so-radio-item"
      >
        <template #label>
          <div
            class="radio-label-wrapper"
            :class="{ 'has-disabled-message': item.disabled }"
          >
            <div class="radio-label-container">
              <span>{{ item.text }}</span>
              <SoStatus
                v-if="item.status"
                :status="{ type: 'normal', text: item.status.text }"
                class="radio-status"
              />
            </div>
            <div
              v-if="item.disabled && item.disabledRadioMessage"
              class="disabled-radio-message"
            >
              {{ item.disabledRadioMessage }}
            </div>
          </div>
        </template>
      </v-radio>
    </v-radio-group>
    <SoButton
      v-if="clearable && internalValue"
      title="Clear selection"
      variant="text"
      @click="clearSelection"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { ValidationRule } from "@/types/ValidationRule"
  import { SoRadioGroupItem } from "@/types/components/SoRadioGroup"
  import SoStatus from "../SoStatus.vue"
  import SoButton from "./SoButton.vue"

  export default defineComponent({
    components: {
      SoStatus,
      SoButton,
    },
    inheritAttrs: false,
    props: {
      modelValue: {
        type: [String, Number, null, Boolean] as PropType<
          string | number | null | boolean
        >,
        default: null,
        required: false,
      },
      items: {
        type: [Array, null] as PropType<SoRadioGroupItem[] | null>,
        default: null,
        required: true,
      },
      label: {
        type: String,
        default: null,
        required: false,
      },
      error: {
        type: String,
        default: "",
        required: false,
      },
      disabled: {
        type: Boolean,
        default: false,
        required: false,
      },
      disabledRadioMessage: {
        type: String,
        default: "",
        required: false,
      },
      rules: {
        type: Array as PropType<ValidationRule[]>,
        default: [] as ValidationRule[],
        required: false,
      },
      hideDetails: {
        type: Boolean,
        default: false,
      },
      panel: {
        type: Boolean,
        default: false,
      },
      status: {
        type: Object as PropType<{ text: string; show?: boolean }>,
        default: null,
        required: false,
      },
      clearable: {
        type: Boolean,
        default: false,
        required: false,
      },
    },
    emits: ["update:modelValue"],
    computed: {
      internalValue: {
        get() {
          return this.modelValue
        },
        set(newValue: string | number) {
          this.$emit("update:modelValue", newValue)
        },
      },
      isValid() {
        return !!this.error
      },
    },
    methods: {
      clearSelection() {
        this.internalValue = null
      },
    },
  })
</script>

<style scoped>
  .so-radio-panel {
    background-color: rgb(var(--v-theme-surface));
    padding-right: 4px;
    margin-top: 8px;
    border-radius: 4px;
    border: 1px solid rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
    transition: border 250ms;
  }
  .so-radio-panel:hover {
    border: 1px solid rgb(var(--v-theme-on-surface));
  }
  .so-radio-panel__error {
    border: 1px solid rgb(var(--v-theme-error));
  }
  .so-radio-panel__error:hover {
    border: 1px solid rgb(var(--v-theme-error));
  }
  .so-radio-panel__disabled-message :deep(.v-selection-control__input) {
    align-items: start;
  }
  .apply-panel-focus :deep(.v-selection-control--focused) {
    outline: var(--theme-outline);
    border-radius: var(--theme-radius);
  }
  .radio-label-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .radio-label-container span {
    display: inline-block;
  }
  .radio-label-wrapper:has(.disabled-radio-message) .radio-label-container span,
  .radio-label-wrapper.has-disabled-message .radio-label-container span {
    margin-top: 4px;
    margin-bottom: 2px;
  }
  .radio-status {
    display: inline-flex;
    align-items: center;
  }
  .radio-label-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
  }
  .disabled-radio-message {
    display: block;
    font-size: small;
    align-items: flex;
    margin-top: 2px;
  }
</style>
